// Verified-visitor slice. Owns the landing-form submission and the whole
// email-verification lifecycle:
//   - submitContact  → POST /intake/contact → { sent } (link emailed),
//                       { verified } (verification disabled), or { blocked }.
//   - resendMagicLink → POST /intake/resend (cooldown-guarded re-send).
//   - verifyMagicLink → POST /intake/verify (consume the link, set the cookie).
//   - fetchIntakeSession → GET /intake/session (am I already a verified
//                       visitor? returns my contact + saved draft).
//   - logoutVisitor   → POST /intake/logout (clear the cookie / "not you?").
// A verified visitor holds an httpOnly cookie, so the client only tracks the
// derived flags + the contact/draft the server hands back.
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiFetch } from '../api/public-client.js';

export const submitContact = createAsyncThunk(
  'contact/submit',
  async (contact, { rejectWithValue }) => {
    try {
      const result = await apiFetch('/api/intake/contact', { method: 'POST', body: contact });
      return { ...result, contact };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  },
);

export const resendMagicLink = createAsyncThunk(
  'contact/resend',
  async (contact, { rejectWithValue }) => {
    try {
      return await apiFetch('/api/intake/resend', { method: 'POST', body: contact });
    } catch (err) {
      return rejectWithValue(err.message);
    }
  },
);

export const verifyMagicLink = createAsyncThunk(
  'contact/verify',
  async (token, { rejectWithValue }) => {
    try {
      return await apiFetch('/api/intake/verify', { method: 'POST', body: { token } });
    } catch (err) {
      return rejectWithValue(err.message);
    }
  },
);

export const fetchIntakeSession = createAsyncThunk(
  'contact/session',
  async (_arg, { rejectWithValue }) => {
    try {
      return await apiFetch('/api/intake/session');
    } catch (err) {
      return rejectWithValue(err.message);
    }
  },
);

export const logoutVisitor = createAsyncThunk(
  'contact/logout',
  async (_arg, { rejectWithValue }) => {
    try {
      return await apiFetch('/api/intake/logout', { method: 'POST' });
    } catch (err) {
      return rejectWithValue(err.message);
    }
  },
);

// Discards the server-side resume draft (e.g. "start a new request"). Best-
// effort: the local draft is cleared regardless of the network result.
export const discardDraft = createAsyncThunk(
  'contact/discardDraft',
  async (_arg, { rejectWithValue }) => {
    try {
      return await apiFetch('/api/intake/draft', { method: 'DELETE' });
    } catch (err) {
      return rejectWithValue(err.message);
    }
  },
);

const initialState = {
  submitting: false,
  error: null,
  blocked: false,
  verified: false, // holds a valid verified-visitor cookie
  sent: false, // a magic link was emailed and we're awaiting verification
  sessionChecked: false, // fetchIntakeSession has resolved at least once
  contact: null, // { name, email, organization, role }
  pendingEmail: null, // the address a link was sent to (for the interstitial)
  referenceCode: null, // non-secret code echoed in the email + interstitial
  draft: null, // server-side resume draft, if any
  verifyError: null, // 'expired' | 'used' | 'invalid' from the verify screen
};

const contactSlice = createSlice({
  name: 'contact',
  initialState,
  reducers: {
    resetContact: () => initialState,
    clearDraft: (s) => { s.draft = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(submitContact.pending, (s) => {
        s.submitting = true;
        s.error = null;
        s.blocked = false;
      })
      .addCase(submitContact.fulfilled, (s, a) => {
        s.submitting = false;
        if (a.payload.blocked) {
          s.blocked = true;
        } else if (a.payload.verified) {
          // Verification disabled for this deployment: admitted immediately.
          s.verified = true;
          s.contact = a.payload.contact;
        } else {
          // A magic link was emailed.
          s.sent = true;
          s.contact = a.payload.contact;
          s.pendingEmail = a.payload.email;
          s.referenceCode = a.payload.referenceCode;
        }
      })
      .addCase(submitContact.rejected, (s, a) => {
        s.submitting = false;
        s.error = a.payload || 'Something went wrong. Please try again.';
      })
      .addCase(resendMagicLink.pending, (s) => { s.error = null; })
      .addCase(resendMagicLink.fulfilled, (s, a) => {
        if (a.payload.blocked) {
          s.blocked = true;
          s.sent = false;
        } else {
          s.sent = true;
          s.referenceCode = a.payload.referenceCode;
        }
      })
      .addCase(resendMagicLink.rejected, (s, a) => {
        s.error = a.payload || 'Could not resend the link. Please try again.';
      })
      .addCase(verifyMagicLink.pending, (s) => {
        s.submitting = true;
        s.verifyError = null;
      })
      .addCase(verifyMagicLink.fulfilled, (s, a) => {
        s.submitting = false;
        if (a.payload.verified) {
          s.verified = true;
          s.sent = false;
          s.contact = a.payload.contact;
          s.draft = a.payload.draft || null;
        } else {
          s.verifyError = a.payload.error || 'invalid';
        }
      })
      .addCase(verifyMagicLink.rejected, (s, a) => {
        s.submitting = false;
        s.verifyError = 'invalid';
        s.error = a.payload || null;
      })
      .addCase(fetchIntakeSession.fulfilled, (s, a) => {
        s.sessionChecked = true;
        if (a.payload.verified) {
          s.verified = true;
          s.contact = a.payload.contact;
          s.draft = a.payload.draft || null;
        }
      })
      .addCase(fetchIntakeSession.rejected, (s) => {
        s.sessionChecked = true;
      })
      .addCase(discardDraft.pending, (s) => { s.draft = null; })
      .addCase(logoutVisitor.fulfilled, () => initialState);
  },
});

export const { resetContact, clearDraft } = contactSlice.actions;
export default contactSlice.reducer;
